import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useOrg } from '../contexts/OrgContext';
import { useAuth } from '../contexts/AuthContext';
import { getFloc } from '../services/flocService';
import { getAsset, updateAsset } from '../services/assetService';
import { uploadAssetPhoto } from '../services/storageService';
import { FunctionalLocation, Asset } from '@easydone/shared';
import { Timestamp } from 'firebase/firestore';
import { CameraIcon, DocumentTextIcon, CheckIcon } from '@heroicons/react/24/outline';
import Tesseract from 'tesseract.js';

export function NameplateCapturePage() {
  const { flocId } = useParams<{ flocId: string }>();
  const [searchParams] = useSearchParams();
  const assetIdParam = searchParams.get('assetId');
  const { currentOrg } = useOrg();
  const { currentUser } = useAuth();
  const navigate = useNavigate();

  const [floc, setFloc] = useState<FunctionalLocation | null>(null);
  const [assets, setAssets] = useState<Asset[]>([]);
  const [selectedAsset, setSelectedAsset] = useState<Asset | null>(null);
  const [nameplatePhoto, setNameplatePhoto] = useState<string>('');
  const [extractedText, setExtractedText] = useState<string>('');
  const [extracting, setExtracting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  const [formData, setFormData] = useState({
    manufacturer: '',
    model: '',
    serialNumber: '',
    yearManufactured: '',
    // Additional technical specs can be stored in metadata
    powerRating: '',
    voltage: '',
    speed: '',
  });

  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    loadData();
  }, [flocId, currentOrg]);

  const loadData = async () => {
    if (!flocId || !currentOrg) return;

    setLoading(true);
    try {
      // Load FLOC details
      const flocData = await getFloc(currentOrg.id, flocId);
      setFloc(flocData);

      // Load assets at this FLOC
      // Note: You'll need to implement a query to get assets by flocId
      // For now, if assetId is provided in URL, load that specific asset
      if (assetIdParam) {
        const assetData = await getAsset(currentOrg.id, assetIdParam);
        if (assetData) {
          setAssets([assetData]);
          setSelectedAsset(assetData);
          // Pre-fill form with existing data
          setFormData({
            manufacturer: assetData.manufacturer || '',
            model: assetData.model || '',
            serialNumber: assetData.serialNumber || '',
            yearManufactured: assetData.yearManufactured?.toString() || '',
            powerRating: assetData.metadata?.powerRating || '',
            voltage: assetData.metadata?.voltage || '',
            speed: assetData.metadata?.speed || '',
          });
        }
      }
    } catch (error) {
      console.error('Failed to load data:', error);
      alert('Failed to load FLOC data');
    } finally {
      setLoading(false);
    }
  };

  const handlePhotoCapture = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Read file as data URL for preview
    const reader = new FileReader();
    reader.onload = async (e) => {
      const dataUrl = e.target?.result as string;
      setNameplatePhoto(dataUrl);

      // Perform OCR
      setExtracting(true);
      try {
        const result = await Tesseract.recognize(dataUrl, 'eng', {
          logger: (m) => console.log(m),
        });

        const text = result.data.text;
        setExtractedText(text);

        // Try to extract common fields from the text
        extractFieldsFromText(text);
      } catch (error) {
        console.error('OCR failed:', error);
        alert('Failed to extract text from image. You can manually enter the information.');
      } finally {
        setExtracting(false);
      }
    };
    reader.readAsDataURL(file);
  };

  const extractFieldsFromText = (text: string) => {
    const lines = text.split('\n').map((line) => line.trim()).filter(Boolean);

    // Try to extract manufacturer (often first line or has "MFG", "Manufacturer")
    const mfgLine = lines.find((line) =>
      /manufacturer|mfg|made by/i.test(line)
    );
    if (mfgLine) {
      const mfg = mfgLine.replace(/manufacturer|mfg|made by/i, '').replace(/[:]/g, '').trim();
      setFormData((prev) => ({ ...prev, manufacturer: mfg }));
    }

    // Try to extract model (often has "Model", "Type")
    const modelLine = lines.find((line) =>
      /model|type/i.test(line)
    );
    if (modelLine) {
      const model = modelLine.replace(/model|type/i, '').replace(/[:]/g, '').trim();
      setFormData((prev) => ({ ...prev, model }));
    }

    // Try to extract serial number (often has "Serial", "S/N", "SN")
    const serialLine = lines.find((line) =>
      /serial|s\/n|sn|ser/i.test(line) && !/number/i.test(line)
    );
    if (serialLine) {
      const serial = serialLine.replace(/serial|s\/n|sn|ser/i, '').replace(/[:]/g, '').trim();
      setFormData((prev) => ({ ...prev, serialNumber: serial }));
    }

    // Try to extract year (4-digit number, often 19xx or 20xx)
    const yearMatch = text.match(/\b(19\d{2}|20\d{2})\b/);
    if (yearMatch) {
      setFormData((prev) => ({ ...prev, yearManufactured: yearMatch[0] }));
    }

    // Try to extract voltage (often has "V", "Volts", "Voltage")
    const voltageMatch = text.match(/(\d+)\s*(v|volts?|voltage)/i);
    if (voltageMatch) {
      setFormData((prev) => ({ ...prev, voltage: voltageMatch[1] + 'V' }));
    }

    // Try to extract power rating (often has "HP", "kW", "Watts")
    const powerMatch = text.match(/(\d+\.?\d*)\s*(hp|kw|watts?)/i);
    if (powerMatch) {
      setFormData((prev) => ({ ...prev, powerRating: powerMatch[0] }));
    }

    // Try to extract speed (often has "RPM")
    const speedMatch = text.match(/(\d+)\s*(rpm|r\/min)/i);
    if (speedMatch) {
      setFormData((prev) => ({ ...prev, speed: speedMatch[0] }));
    }
  };

  const handleSave = async () => {
    if (!selectedAsset || !currentOrg || !currentUser) return;

    setSaving(true);
    try {
      // Upload nameplate photo
      let photoUrl = '';
      let photoBlobSize = 0;
      if (nameplatePhoto) {
        const photoBlob = await fetch(nameplatePhoto).then((r) => r.blob());
        photoBlobSize = photoBlob.size;
        const file = new File([photoBlob], 'nameplate.jpg', { type: 'image/jpeg' });
        photoUrl = await uploadAssetPhoto(currentOrg.id, selectedAsset.id, file);
      }

      // Prepare update data
      const updateData: Record<string, any> = {
        manufacturer: formData.manufacturer || null,
        model: formData.model || null,
        serialNumber: formData.serialNumber || selectedAsset.serialNumber,
        yearManufactured: formData.yearManufactured ? parseInt(formData.yearManufactured) : null,
        metadata: {
          ...selectedAsset.metadata,
          powerRating: formData.powerRating,
          voltage: formData.voltage,
          speed: formData.speed,
        },
      };

      // Add nameplate photo to photoAttachments
      if (photoUrl) {
        const nameplatePhotoAttachment = {
          id: `nameplate-${Date.now()}`,
          name: 'Nameplate',
          url: photoUrl,
          type: 'image/jpeg',
          size: photoBlobSize,
          uploadedAt: Timestamp.fromDate(new Date()),
          uploadedBy: currentUser.uid,
          isNameplate: true,
        };

        updateData.photoAttachments = [
          ...(selectedAsset.photoAttachments || []),
          nameplatePhotoAttachment,
        ];
      }

      await updateAsset(currentOrg.id, selectedAsset.id, updateData, currentUser.uid);

      alert('Asset information updated successfully!');
      navigate(`/assets/${selectedAsset.id}`);
    } catch (error) {
      console.error('Failed to save asset information:', error);
      alert('Failed to save asset information');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Loading...</div>
      </div>
    );
  }

  if (!floc) {
    return (
      <div className="max-w-4xl mx-auto">
        <div className="text-center text-gray-500 py-8">FLOC not found</div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Capture Nameplate</h1>
        <p className="text-gray-600 mt-1">
          FLOC: {floc.code} - {floc.name}
        </p>
      </div>

      {!selectedAsset ? (
        <div className="card">
          <h2 className="text-lg font-semibold mb-4">Select Asset</h2>
          {assets.length > 0 ? (
            <div className="space-y-2">
              {assets.map((asset) => (
                <button
                  key={asset.id}
                  onClick={() => setSelectedAsset(asset)}
                  className="w-full text-left p-4 border border-gray-200 rounded-lg hover:bg-gray-50"
                >
                  <p className="font-medium">{asset.name}</p>
                  <p className="text-sm text-gray-600">
                    Serial: {asset.serialNumber} • Type: {asset.type}
                  </p>
                </button>
              ))}
            </div>
          ) : (
            <p className="text-gray-500">No assets found at this FLOC</p>
          )}
        </div>
      ) : (
        <div className="space-y-6">
          {/* Selected Asset */}
          <div className="card">
            <h2 className="text-lg font-semibold mb-2">Selected Asset</h2>
            <p className="text-gray-900">{selectedAsset.name}</p>
            <p className="text-sm text-gray-600">Serial: {selectedAsset.serialNumber}</p>
          </div>

          {/* Capture Nameplate Photo */}
          {!nameplatePhoto && (
            <div className="card">
              <h2 className="text-lg font-semibold mb-4 flex items-center">
                <CameraIcon className="w-5 h-5 mr-2" />
                Capture Nameplate Photo
              </h2>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                capture="environment"
                onChange={handlePhotoCapture}
                className="hidden"
              />
              <button
                onClick={() => fileInputRef.current?.click()}
                className="btn btn-primary w-full"
              >
                Take Photo
              </button>
            </div>
          )}

          {/* Photo Preview & OCR */}
          {nameplatePhoto && (
            <div className="card">
              <h2 className="text-lg font-semibold mb-4">Nameplate Photo</h2>
              <img
                src={nameplatePhoto}
                alt="Nameplate"
                className="w-full rounded-lg border border-gray-200 mb-4"
              />
              {extracting && (
                <p className="text-gray-600 text-sm">Extracting text from image...</p>
              )}
              {extractedText && (
                <details className="mt-4">
                  <summary className="cursor-pointer text-sm font-medium text-gray-700">
                    View Extracted Text
                  </summary>
                  <pre className="mt-2 p-3 bg-gray-50 rounded text-xs overflow-auto max-h-40">
                    {extractedText}
                  </pre>
                </details>
              )}
              <button
                onClick={() => {
                  setNameplatePhoto('');
                  setExtractedText('');
                }}
                className="btn btn-secondary text-sm mt-2"
              >
                Retake Photo
              </button>
            </div>
          )}

          {/* Information Form */}
          {nameplatePhoto && !extracting && (
            <div className="card">
              <h2 className="text-lg font-semibold mb-4 flex items-center">
                <DocumentTextIcon className="w-5 h-5 mr-2" />
                Asset Information
              </h2>
              <p className="text-sm text-gray-600 mb-4">
                Review and edit the extracted information. Fill in any missing fields.
              </p>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Manufacturer
                  </label>
                  <input
                    type="text"
                    value={formData.manufacturer}
                    onChange={(e) => setFormData({ ...formData, manufacturer: e.target.value })}
                    className="input w-full"
                    placeholder="Enter manufacturer"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Model Number
                  </label>
                  <input
                    type="text"
                    value={formData.model}
                    onChange={(e) => setFormData({ ...formData, model: e.target.value })}
                    className="input w-full"
                    placeholder="Enter model number"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Serial Number
                  </label>
                  <input
                    type="text"
                    value={formData.serialNumber}
                    onChange={(e) => setFormData({ ...formData, serialNumber: e.target.value })}
                    className="input w-full"
                    placeholder="Enter serial number"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Year Manufactured
                  </label>
                  <input
                    type="text"
                    value={formData.yearManufactured}
                    onChange={(e) => setFormData({ ...formData, yearManufactured: e.target.value })}
                    className="input w-full"
                    placeholder="e.g., 2020"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Power Rating
                    </label>
                    <input
                      type="text"
                      value={formData.powerRating}
                      onChange={(e) => setFormData({ ...formData, powerRating: e.target.value })}
                      className="input w-full"
                      placeholder="e.g., 10 HP"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Voltage
                    </label>
                    <input
                      type="text"
                      value={formData.voltage}
                      onChange={(e) => setFormData({ ...formData, voltage: e.target.value })}
                      className="input w-full"
                      placeholder="e.g., 480V"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Speed
                    </label>
                    <input
                      type="text"
                      value={formData.speed}
                      onChange={(e) => setFormData({ ...formData, speed: e.target.value })}
                      className="input w-full"
                      placeholder="e.g., 1800 RPM"
                    />
                  </div>
                </div>
              </div>

              <div className="mt-6 flex gap-3">
                <button
                  onClick={handleSave}
                  disabled={saving}
                  className="btn btn-primary flex items-center"
                >
                  <CheckIcon className="w-5 h-5 mr-2" />
                  {saving ? 'Saving...' : 'Save Information'}
                </button>
                <button
                  onClick={() => navigate(`/flocs/${flocId}`)}
                  className="btn btn-secondary"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
